import { FormEvent, useState } from 'react';
import {
  Mail,
  Lock,
  CheckCircle2,
  Loader2,
  Fingerprint,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { loginWithPasskey, webauthnSupported } from '../api/webauthn';

type Mode = 'password' | 'magic';

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useAuthStore((s) => s.login);
  const loggingIn = useAuthStore((s) => s.loggingIn);
  const requestLink = useAuthStore((s) => s.requestLink);
  const resetFlow = useAuthStore((s) => s.resetFlow);
  const linkRequested = useAuthStore((s) => s.linkRequested);
  const requesting = useAuthStore((s) => s.requesting);
  const authError = useAuthStore((s) => s.authError);

  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);

  const onBiometric = async () => {
    setBioBusy(true);
    setBioError(null);
    try {
      await loginWithPasskey();
    } catch {
      setBioError(
        'Connexion biométrique impossible. Utilisez votre mot de passe, ou activez FaceID depuis votre espace après connexion.',
      );
    } finally {
      setBioBusy(false);
    }
  };

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    void login(email.trim(), password);
  };

  const onSubmitMagic = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void requestLink(email.trim());
  };

  const switchMode = (next: Mode) => {
    resetFlow();
    setBioError(null);
    setMode(next);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo-permio.png" alt="Permio" className="h-9 w-auto" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {/* ── Confirmation d'envoi du lien magique ── */}
          {mode === 'magic' && linkRequested ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="text-green-600" size={28} />
              </div>
              <div>
                <h1 className="text-slate-900">Vérifiez votre boîte mail</h1>
                <p className="text-slate-600 mt-2 text-sm">
                  Si un compte candidat est associé à cette adresse, un lien de
                  connexion vient d’être envoyé. Il est valable 15 minutes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => switchMode('password')}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                Revenir à la connexion par mot de passe
              </button>
            </div>
          ) : mode === 'password' ? (
            /* ── Connexion email + mot de passe (par défaut) ── */
            <>
              <div className="text-center mb-6">
                <h1 className="text-slate-900">Mon espace candidat</h1>
                <p className="text-slate-600 mt-2 text-sm">
                  Connectez-vous avec votre email et votre mot de passe.
                </p>
              </div>

              <form onSubmit={onSubmitPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-slate-900"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Mot de passe
                  </label>
                  <div className="relative">
                    <Lock
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-slate-900"
                    />
                  </div>
                </div>

                {authError && (
                  <p className="text-sm text-red-600" role="alert">
                    {authError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loggingIn}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
                >
                  {loggingIn && <Loader2 size={16} className="animate-spin" />}
                  Se connecter
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => switchMode('magic')}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  Mot de passe oublié ou première connexion ? Recevoir un lien
                  par email
                </button>
              </div>

              {webauthnSupported() && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="h-px bg-slate-200 flex-1" />
                    <span className="text-xs text-slate-400">ou</span>
                    <div className="h-px bg-slate-200 flex-1" />
                  </div>
                  <button
                    type="button"
                    onClick={onBiometric}
                    disabled={bioBusy}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-800 text-sm font-semibold rounded-lg transition-colors"
                  >
                    {bioBusy ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Fingerprint size={16} className="text-indigo-600" />
                    )}
                    Se connecter avec FaceID / Touch ID
                  </button>
                  {bioError && (
                    <p className="text-xs text-red-600 mt-2" role="alert">
                      {bioError}
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            /* ── Connexion par lien magique (secondaire) ── */
            <>
              <div className="text-center mb-6">
                <h1 className="text-slate-900">Connexion par email</h1>
                <p className="text-slate-600 mt-2 text-sm">
                  Saisissez votre email : nous vous enverrons un lien de
                  connexion sécurisé, sans mot de passe. Vous pourrez ensuite
                  définir un mot de passe depuis votre espace.
                </p>
              </div>

              <form onSubmit={onSubmitMagic} className="space-y-4">
                <div>
                  <label
                    htmlFor="magic-email"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      id="magic-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-slate-900"
                    />
                  </div>
                </div>

                {authError && (
                  <p className="text-sm text-red-600" role="alert">
                    {authError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={requesting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
                >
                  {requesting && <Loader2 size={16} className="animate-spin" />}
                  Recevoir mon lien de connexion
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  Revenir à la connexion par mot de passe
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Un souci de connexion ? Contactez votre auto-école.
        </p>
      </div>
    </div>
  );
}
